import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Teleprompter from "@/components/Teleprompter";
import { BRAND_WORDMARK_VAZADA } from "@/lib/brand";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Public teleprompter page — NO authentication. Reads only the retranca's
// title + text via the public token endpoint. Shares the Teleprompter
// component with camera/recording (download-only).
export default function PublicTeleprompterPage() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | active | error
  const [status, setStatus] = useState("");
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND}/api/public/tp/${token}`);
        if (cancelled) return;
        setData(r.data);
        setStatus(r.data.status);
        setState(r.data.status === "active" ? "active" : "error");
      } catch (e) {
        if (cancelled) return;
        setStatus(e.response?.status === 404 ? "not_found" : "error");
        setState("error");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (state === "loading") {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-300 flex items-center justify-center">
        <div className="text-xs uppercase tracking-[0.3em]">Carregando teleprompter...</div>
      </div>
    );
  }

  if (state === "error") {
    const msg = status === "revoked" ? "Este teleprompter não está mais disponível. Solicite um novo link à redação."
      : status === "expired" ? "Este link expirou. Solicite um novo à redação."
      : status === "deleted" ? "A lauda vinculada foi removida."
      : "Este teleprompter não está mais disponível.";
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-200 flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-4">
          <img src={BRAND_WORDMARK_VAZADA} alt="PodiUmScript" className="w-48 mx-auto invert" />
          <div className="font-heading text-2xl font-bold tracking-tight">Link indisponível</div>
          <p className="text-sm text-zinc-400 leading-relaxed" data-testid="public-tp-unavailable">{msg}</p>
        </div>
      </div>
    );
  }

  // Build a minimal "episode" shape compatible with the Teleprompter component
  const pseudoEpisode = {
    id: `public-${token}`,
    title: data.retranca_title || "Teleprompter",
    retrancas: [
      {
        id: `public-r-${token}`,
        title: data.retranca_title || "",
        type: "VT",
        presenter: "",
        text: data.retranca_text || "",
        uppercase: !!data.uppercase,
        approved: true,
        vt_duration: 0,
      },
    ],
  };

  if (!open) {
    return (
      <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center p-6">
        <div className="max-w-md w-full space-y-6 text-center">
          <img src={BRAND_WORDMARK_VAZADA} alt="PodiUmScript" className="w-48 mx-auto invert" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-amber-400 mb-2">
              Teleprompter compartilhado
            </div>
            <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="public-tp-title">
              {data.retranca_title || "Sem título"}
            </h1>
          </div>
          <button
            data-testid="public-tp-start"
            onClick={() => setOpen(true)}
            className="w-full h-12 bg-[#E8431E] hover:bg-[#C73815] rounded-sm text-white font-bold uppercase tracking-[0.2em] text-sm"
          >
            Abrir teleprompter
          </button>
          <p className="text-xs text-zinc-500">
            Ajuste a velocidade, o tamanho da fonte e grave diretamente do seu dispositivo.
          </p>
        </div>
      </div>
    );
  }

  return (
    <Teleprompter
      episode={pseudoEpisode}
      onClose={() => setOpen(false)}
      onExportPdf={() => {}}
      syncToken={token}
      allowShare={false}
    />
  );
}
